import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { FileTransfer } from '@ionic-native/file-transfer';
import { File } from '@ionic-native/file';
import { FileOpener } from '@ionic-native/file-opener';
import { DocumentViewer, DocumentViewerOptions } from '@ionic-native/document-viewer';
import { SpinnerProvider } from '../spinner/spinner';
import { AlertProvider } from '../alert/alert';

/**
 * Contexto necessário para exibir um PDF de checklist.
 * `url` já deve vir montada (ConfigHelper.UrlCheckList + impressao + codCheckList).
 * `codCheckList`/`placa`/`chassi` são usados apenas para o nome do arquivo no download.
 */
export interface PdfContexto {
  url: string;
  codCheckList: string | number;
  placa?: string;
  chassi?: string;
}

/**
 * Provider compartilhado para exibição de PDF do checklist.
 * Os corpos de visualizarPDF/abrirPdf são uma réplica fiel de checklist.ts (5389-5490),
 * parametrizados via PdfContexto em vez do estado da página.
 */
@Injectable()
export class PdfProvider {

  constructor(
    private transfer: FileTransfer,
    private file: File,
    private fileOpener: FileOpener,
    private documentViewer: DocumentViewer,
    private platform: Platform,
    private spinnerSrv: SpinnerProvider,
    private alertSrv: AlertProvider
  ) {}

  async visualizarPDF(ctx: PdfContexto, email = 0, recepcaoemail = 0) {
    let urlpdf = ctx.url;

    if (email === 1) urlpdf += "&email=1";
    if (recepcaoemail === 1) urlpdf += "&recepcaoemail=1";
    if (recepcaoemail === 2) urlpdf += "&recepcaoemail=0";

    const isCordova = this.platform.is('cordova');

    if (!isCordova) {
      // Web fallback
      window.open(urlpdf, '_blank');
      return;
    }

    const transfer = this.transfer.create();
    const path = this.platform.is('ios') ? this.file.documentsDirectory : this.file.dataDirectory;
    const nome_arquivo = ctx.placa ? `_Placa_${ctx.placa}` : `_Chassi_${ctx.chassi}`;
    const fileName = `Checklist_${ctx.codCheckList}${nome_arquivo}.pdf`;

    this.spinnerSrv.Show("Fazendo download do checklist...");

    try {
      const entry = await transfer.download(urlpdf, path + fileName);

      // Função para verificar se o arquivo existe
      const checkFileExists = (filePath: string): Promise<boolean> => {
        return new Promise((resolve) => {
          (window as any).resolveLocalFileSystemURL(
            filePath,
            () => resolve(true),
            () => resolve(false)
          );
        });
      };

      const exists = await checkFileExists(entry.nativeURL);
      const fileToOpen = exists ? entry.nativeURL : entry.toURL();

      await this.fileOpener.open(fileToOpen, 'application/pdf');
      this.spinnerSrv.Hide();

    } catch (error) {
      console.log("Erro ao abrir PDF", error);
      this.spinnerSrv.Hide();
      this.alertSrv.newAlert(
        "erro",
        "Não foi possível abrir o PDF",
        "",
        "Tente novamente, se o problema persistir informar ao setor de tecnologia"
      );
    }
  }

  async abrirPdf(ctx: PdfContexto) {
    const urlpdf = ctx.url;
    const isCordova = this.platform.is('cordova');

    if (!isCordova) {
      // Fallback web
      window.open(urlpdf, '_blank');
      return;
    }

    const transfer = this.transfer.create();
    const path = this.platform.is('ios') ? this.file.documentsDirectory : this.file.dataDirectory;
    const nome_arquivo = ctx.placa ? `_Placa_${ctx.placa}` : `_Chassi_${ctx.chassi}`;
    const fileName = `Checklist_${ctx.codCheckList}${nome_arquivo}.pdf`;

    this.spinnerSrv.Show("Fazendo download do checklist...");

    try {
      const entry = await transfer.download(urlpdf, path + fileName);

      // Verifica se o arquivo existe
      const fileExists = (filePath: string) =>
        new Promise<boolean>((resolve) => {
          (window as any).resolveLocalFileSystemURL(
            filePath,
            () => resolve(true),
            () => resolve(false)
          );
        });

      const exists = await fileExists(entry.nativeURL);
      const fileToOpen = exists ? entry.nativeURL : entry.toURL();

      const options: DocumentViewerOptions = {};
      this.documentViewer.viewDocument(fileToOpen, 'application/pdf', options);
      this.spinnerSrv.Hide();

    } catch (error) {
      console.log("Erro ao abrir PDF", error);
      this.spinnerSrv.Hide();
      this.alertSrv.newAlert(
        "erro",
        "Não foi possível abrir o PDF",
        "",
        "Tente novamente, se o problema persistir informar ao setor de tecnologia"
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/pdf/pdf.ts